import React from "react";
import { graphql, useFragment } from "react-relay";

import cx from "classnames";

import { NoteIdContext } from "./modal";
import { type CardActionProps, CreateCard, TemplateCard } from "./TemplateCard";

import type { GridElementTemplate$data, GridElementTemplate$key } from "~/__generated__/GridElementTemplate.graphql";
import type { GridNoteTemplates$key } from "~/__generated__/GridNoteTemplates.graphql";

import styles from "./css/Grid.module.css";

interface Props {
	templates: GridNoteTemplates$key;
	preview: (id: string) => void;
	onApply?: (id: string) => void;
	showCreateCard?: boolean;
	onClickCreate?: () => void;
	dropdownMenuActions?: (template: GridElementTemplate$data) => CardActionProps["cardDropdownMenuActions"];
	className?: string;
	selected?: string;
}

function GridElement({
	preview,
	onApply,
	template: templateKey,
	dropdownMenuActions,
	selected,
}: {
	onApply?: (id: string) => void;
	preview: (id: string) => void;
	template: GridElementTemplate$key;
	dropdownMenuActions?: Props["dropdownMenuActions"];
	selected?: boolean;
}) {
	const noteId = React.useContext(NoteIdContext);
	const template = useFragment(
		graphql`
			fragment GridElementTemplate on NoteTemplateNode {
				id
				viewerCanEdit
				starred
				visibility
				...TemplateCardFullsizeNoteTemplate
			}
		`,
		templateKey,
	);

	const apply = React.useCallback(() => onApply?.(template.id), [onApply, template]);
	const doPreview = React.useCallback(() => preview(template.id), [preview, template]);

	const cardDropdownMenuActions: CardActionProps["cardDropdownMenuActions"] = React.useMemo(() => {
		return dropdownMenuActions?.(template);
	}, [template, dropdownMenuActions]);

	let actionProps: CardActionProps;
	if (noteId) {
		actionProps = {
			withActions: true,
			apply,
			preview: doPreview,
			onClick: doPreview,
		};
	} else {
		actionProps = { onClick: doPreview, cardDropdownMenuActions };
	}

	return (
		<li key={template.id} className={styles.gridItem}>
			<TemplateCard template={template} selected={selected} {...actionProps} />
		</li>
	);
}

export function TemplateGrid({
	templates: templatesKey,
	preview,
	onApply,
	showCreateCard,
	onClickCreate,
	dropdownMenuActions,
	className,
	selected,
}: Props) {
	const templates = useFragment(
		graphql`
			fragment GridNoteTemplates on NoteTemplateNode @relay(plural: true) {
				id
				...GridElementTemplate
			}
		`,
		templatesKey,
	);

	return (
		<ul className={cx(styles.grid, className)}>
			{templates.map(t => (
				<GridElement
					key={t.id}
					template={t}
					preview={preview}
					onApply={onApply}
					dropdownMenuActions={dropdownMenuActions}
					selected={t.id === selected}
				/>
			))}
			{showCreateCard && <CreateCard onClick={onClickCreate} />}
		</ul>
	);
}
